'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
interface UsePointsAnimationOptions {
  currentValue: number;
  animationDuration?: number; // в миллисекундах
}
interface UsePointsAnimationReturn {
  animatedValue: number;
  isAnimating: boolean;
  triggerAnimation: (newValue: number) => void;
}

/**
 * Хук для отслеживания изменений поинтов и запуска анимации
 * @param options - Опции хука
 * @returns Объект с анимированным значением, флагом анимации и функцией для запуска анимации
 */
export const usePointsAnimation = t0 => {
  const $ = _c(12);
  const {
    currentValue,
    animationDuration: t1
  } = t0;
  const animationDuration = t1 === undefined ? 3000 : t1;
  const [animatedValue, setAnimatedValue] = useState(currentValue);
  const [isAnimating, setIsAnimating] = useState(false);
  const previousValueRef = useRef(currentValue);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(null);
  const animationDurationRef = useRef(animationDuration);
  let t2;
  let t3;
  if ($[0] !== animationDuration) {
    t2 = () => {
      animationDurationRef.current = animationDuration;
    };
    t3 = [animationDuration];
    $[0] = animationDuration;
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = newValue => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      const startValue = previousValueRef.current;
      const endValue = newValue;
      const difference = endValue - startValue;
      if (difference === 0) {
        setAnimatedValue(newValue);
        return;
      }
      setIsAnimating(true);
      startTimeRef.current = Date.now();
      const animate = () => {
        if (!startTimeRef.current) {
          return;
        }
        const elapsed = Date.now() - startTimeRef.current;
        const progress = Math.min(elapsed / animationDurationRef.current, 1);
        const easeOutCubic = _temp;
        const easedProgress = easeOutCubic(progress);
        const currentAnimatedValue = startValue + difference * easedProgress;
        setAnimatedValue(currentAnimatedValue);
        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(animate);
        } else {
          setAnimatedValue(endValue);
          setIsAnimating(false);
          startTimeRef.current = null;
          animationFrameRef.current = null;
        }
      };
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const triggerAnimation = t4;
  let t5;
  let t6;
  if ($[4] !== currentValue) {
    t5 = () => {
      if (previousValueRef.current !== currentValue) {
        triggerAnimation(currentValue);
      }
      previousValueRef.current = currentValue;
    };
    t6 = [currentValue, triggerAnimation];
    $[4] = currentValue;
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
    t8 = [];
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  useEffect(t7, t8);
  let t9;
  if ($[9] !== animatedValue || $[10] !== isAnimating) {
    t9 = {
      animatedValue,
      isAnimating,
      triggerAnimation
    };
    $[9] = animatedValue;
    $[10] = isAnimating;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  return t9;
};
function _temp(t) {
  return 1 - (1 - t) ** 3;
}